import type { Order, OrderItem } from "../types";

/**
 * HA-885 / HA-658 (shared) — whether the till offers a change to an order's money (apply, modify or remove a discount; a comp next).
 * The server's guard refuses the same cases (OrderMoneyChangeGuard); this only keeps the controls off when it would. The Order must
 * be Open, not frozen, not split, hold no Completed or Pending payment, and not be on the Payment screen. Permission is a separate
 * gate (the `staff-terminal-discount` operation); this is about the order.
 */
export function canChangeOrderMoney(
	order:
		| Pick<Order, "status" | "isFrozen" | "activeSplitGroup" | "payments">
		| null
		| undefined,
	options: { onPaymentScreen?: boolean } = {},
): boolean {
	if (!order || options.onPaymentScreen) return false;
	if (order.status !== "Open") return false;
	if (order.isFrozen) return false;
	if (order.activeSplitGroup) return false;
	return !order.payments.some(
		(p) => p.status === "Completed" || p.status === "Pending",
	);
}

/** A comped line can't be discounted (HA-658): its own Apply Discount stays off while whole-order Apply is still offered. */
export function canDiscountLine(
	canChangeMoney: boolean,
	line: Pick<OrderItem, "status"> & { comped?: boolean },
): boolean {
	return canChangeMoney && !line.comped && line.status !== "Cancelled";
}

/** Whether the order carries anything that makes the till's own placeholder totals wrong (discounts now; comps when HA-658 reaches the till). */
export function hasMoneyAdjustments(
	order: Pick<Order, "discounts"> & { comps?: unknown[] },
): boolean {
	return (order.discounts?.length ?? 0) > 0 || (order.comps?.length ?? 0) > 0;
}

type Totals = Pick<Order, "subtotal" | "tax" | "total">;

/**
 * HA-885 — the placeholder-freeze rule. The till still works out running totals when a line changes, before the server answers.
 * For an order with a discount (or a comp) that figure would be wrong, so the last server figures stay and the order is marked
 * `figuresStale` ("Updating…") until the server's answer replaces them (`withServerFigures` clears the mark). An order with none
 * takes the till's totals as before.
 */
export function withLocalTotals<T extends Order>(order: T, local: Totals): T {
	if (hasMoneyAdjustments(order)) {
		return { ...order, figuresStale: true };
	}
	return { ...order, ...local };
}
